// angular
import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  ClientAccountVisibilityType,
  OrderDataPrivatizationService,
  StaticDataPrivatizationType,
} from "shared/cubiq-services";
// interface-components models
import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";
import {
  ICAutocompleteGenericModel,
  ICCommentsModel,
  ICFixedCcyModel,
  ICFixingSourceModel,
  ICMarkupModel,
  ICSuspendModel,
  ICTenorValueDateModel,
  ICTimeControlModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
// order-entry services
import { OELookupService } from "../basic/oe-lookup.service";
import { OEPermissionsService } from "../basic/oe-permissions.service";
import { OESettingsService } from "../basic/oe-settings.service";
import { OETimeMachineService } from "../basic/oe-time-machine.service";
import { OEUtilityService } from "../basic/oe-utility.service";
import { OEValidatorService } from "../oe-validator.service";
// order-entry models
import { OEDataModel } from "../../data/models/oe-data.model";
import { OEStrategyRefModel } from "../../data/models/oe-strategy.model";
// order-entry enums
import { SuspensionHelper } from "shared/cubiq-services/model/orders/SuspensionHelper";
import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";
import { ATDLNControlType } from "../../data-atdl/enums/atdl-control-type.enum";
import { ATDLDataModel } from "../../data-atdl/model/atdl-data.model";
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";
import { ICDAutocompleteModel } from "../../data-idc/controls/icd-autocomplete.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICDCheckboxModel } from "../../data-idc/controls/icd-checkbox.model";
import { ICDClockModel } from "../../data-idc/controls/icd-clock.model";
import { ICDRadioInputModel } from "../../data-idc/controls/icd-radio-input.model";
import { ICDSpinnerModel } from "../../data-idc/controls/icd-spinner.model";
import { ICDTextFieldModel } from "../../data-idc/controls/icd-text-field.model";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { OENOperationMode } from "../../data/enums/oe-operation-mode.enum";
import { ICLimitPriceModel } from "../../interface/controls/models/ic-limit-price.model";
import { ATDLDatasetService } from "../atdl/atdl-dataset.service";
import { ATDLUtilityService } from "../atdl/atdl-utility.service";
import { OELoggerService } from "../basic/oe-logger.service";
import { OEAdapterGammaHelper } from "./oe-adapter-gamma-helper";
import { DealtPricingService } from "../../../../shared/cubiq-services/lookup/pricing/impl/DealtPricingService";
import { ATDLNParameterType } from "app/order-entry/data-atdl/enums/atdl-parameter-type.enum";
import { FIX_TO_PARAM_MAPPING } from "app/order-entry/data-atdl/mappings/atdl-param-type-to-fix-type.const";
import { ConstraintValue } from "../../../../shared/cubiq-services/model/core/ConstraintValue";
///

const DEFAULT_TRADER_GROUP: number = -1;
const ZERO = new CubiqTS.BigNumber("0");

@Injectable()
export class OEAdapterReverseService {
  private suspensionHelper: SuspensionHelper;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected oeLogger: OELoggerService,
    private permissions: OEPermissionsService,
    private utility: OEUtilityService,
    private validator: OEValidatorService,
    private lookup: OELookupService,
    private settings: OESettingsService,
    private timeMachine: OETimeMachineService,
    private atdlDataset: ATDLDatasetService,
    private atdlUtility: ATDLUtilityService,
    private privatizationService: OrderDataPrivatizationService,
    ///
    private activeUserProfile: ActiveUserProfilePromiseService,
    private gammaAdapterHelper: OEAdapterGammaHelper,
    private pricingService: DealtPricingService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);

    this.suspensionHelper = new SuspensionHelper();
  }

  /**********************************************************************/
  /* ORDER DATA IMPL */

  public adaptOrderDataToOrderEntryModel(
    rootOrderData: CubiqTS.OrderDataImpl,
    mode: OENOperationMode,
    resModel: OEDataModel = null,
  ): Promise<Array<OEDataModel>> {
    return new Promise<Array<OEDataModel>>((resolve, reject) => {
      try {
        let nodeMap: Map<string, CubiqTS.OrderDataImpl> = new Map<string, CubiqTS.OrderDataImpl>();
        let parsedModels: Array<OEDataModel> = new Array<OEDataModel>();

        //	this.sortChildrenData(rootOrderData);
        this.traverseStrategyGraph(rootOrderData, nodeMap, mode);

        if (nodeMap != null && nodeMap.size > 0) {
          //Object.keys(nodeMap).forEach((nodeRefId: string)=>{ });
          this.activeUserProfile.getActiveUserData().then((activeUser: ActiveUser) => {
            // If root order is a call then deduce fixed currency from first TP/SL leg
            if (rootOrderData.orderType === CubiqTS.OrderType.LIMIT_CALL && nodeMap.size > 1) {
              for (const legData of nodeMap.values()) {
                if (
                  legData.orderType === CubiqTS.OrderType.LIMIT_SL ||
                  legData.orderType === CubiqTS.OrderType.LIMIT_TP
                ) {
                  // Change the CALL fixed currency to be that of the leg
                  rootOrderData.fixedCode = legData.fixedCode;
                  break;
                }
              }
            }

            let promiseArray = new Array<Promise<OEDataModel>>();
            for (let [, value] of nodeMap) {
              promiseArray.push(this.parseOrderDataImpl(value, mode, activeUser, resModel));
            }
            Promise.all(promiseArray).then((models: Array<OEDataModel>) => {
              // If any leg has any fill then update all leg models
              let hasStrategyFill = models.some((model: OEDataModel) => {
                return (
                  model &&
                  (model.fillState === CubiqTS.FillState.FILLED ||
                    model.fillState === CubiqTS.FillState.PARTFILLED)
                );
              });

              models.forEach((model: OEDataModel) => {
                if (model != null && model instanceof OEDataModel) {
                  model.hasStrategyFill = hasStrategyFill;
                  parsedModels.push(model);
                }
              });
              resolve(parsedModels);
            });
          });
        } else {
          resolve(null);
        }
      } catch (ex) {
        reject(ex);
      }
    });
  }

  /**
   * Converts a single OrderDataImpl into an OEDataModel and returns the result. Relationships (sibling and children) are not parsed.
   * @param orderData - OrderDataImpl to be parsed
   * @returns OEDataModel based on OrderDataImpl supplied as first argument.
   */
  public parseOrderDataImpl(
    orderData: CubiqTS.OrderDataImpl,
    mode: OENOperationMode,
    activeUser: ActiveUser,
    resModel: OEDataModel = null,
  ): Promise<OEDataModel> {
    return new Promise<OEDataModel>((resolve, reject) => {
      try {
        if (
          orderData != null &&
          orderData instanceof CubiqTS.OrderDataImpl &&
          orderData.clOrdID != null &&
          orderData.clOrdID.length > 1
        ) {
          this.privatizationService.checkPrivatization(orderData);

          let modeIsModify: boolean = mode === OENOperationMode.MODIFY;
          let modeIsDuplicate: boolean = mode === OENOperationMode.DUPLICATE;
          let modeIsModifyInfo: boolean = mode === OENOperationMode.MODIFY_INFO;
          let modeIsAmendMarkup: boolean = mode === OENOperationMode.AMEND_MARKUP;
          let isBenchmark: boolean =
            orderData.requestedOrderType === CubiqTS.RequestOrderType.BMARK;
          let isGamma: boolean = orderData.strategyType === CubiqTS.StrategyType.GAMMA;

          // flow control flags
          let canUserChangeMarkup: boolean = true;
          let useTraderGroupOnDuplicate: boolean = modeIsDuplicate;
          let useMarkupOnDuplicate: boolean = modeIsDuplicate;
          let convertMarkupValueFromDecsToPips: boolean = false;
          //if(modeIsDuplicate){
          let permissions = this.permissions.getSysConfigPermissions();
          if (CubiqTS.OrderType.ALGO === orderData.orderType) {
            canUserChangeMarkup = permissions.enterMarkupAlgo;
            useMarkupOnDuplicate = useMarkupOnDuplicate && permissions.enterMarkupAlgo;
            useTraderGroupOnDuplicate = permissions.enterTraderGroupAlgo;
          } else if (CubiqTS.OrderType.BENCHMARK == orderData.orderType) {
            canUserChangeMarkup = permissions.enterMarkupBMark;
            useMarkupOnDuplicate = useMarkupOnDuplicate && permissions.enterMarkupBMark;
            useTraderGroupOnDuplicate = permissions.enterTraderGroupBenchmark;
          } else {
            canUserChangeMarkup = permissions.enterMarkupLimit;
            useMarkupOnDuplicate = useMarkupOnDuplicate && permissions.enterMarkupLimit;
            useTraderGroupOnDuplicate = permissions.enterTraderGroupLimit;
          }
          //}

          if (isGamma && mode === OENOperationMode.DUPLICATE) {
            let hasSameMarkup: boolean = this.gammaAdapterHelper.hasSameMarkup(orderData);
            useMarkupOnDuplicate = useMarkupOnDuplicate && hasSameMarkup;
          }

          if (
            orderData.ownerGroupIds &&
            orderData.ownerGroupIds.length > 1 &&
            orderData.ownerGroupIds[0] != orderData.ownerGroupIds[1]
          ) {
            // we cannot "modify" tradergroups on a spawned order as there are two
            logger.out.info(
              `Modifying an order with spawned trader groups - so trader group not loaded`,
            );
            useTraderGroupOnDuplicate = false;
          }

          if (orderData.deskId && orderData.deskId.toNumber() == DEFAULT_TRADER_GROUP) {
            logger.out.info(
              `Modifying an order with default trader group - so trader group not loaded`,
            );
            useTraderGroupOnDuplicate = false;
          }
          // isPrivate is only relevant for duplicate mode
          let canWorkWithClient: boolean = this.permissions.determineIfUserCanWorkWithClient(
            activeUser,
            orderData.clientCode,
          );

          let canSeePrivateOrders: boolean =
            orderData.requestedOrderType === CubiqTS.RequestOrderType.BMARK
              ? activeUser.visibilityPrivateBMOrders
              : activeUser.visibilityPrivateLimitOrders;

          let obfuscateClientData: boolean =
            modeIsDuplicate &&
            (!canWorkWithClient ||
              (orderData.isPrivate &&
                orderData.entryUserCode != activeUser.userCode &&
                !canSeePrivateOrders));

          let model =
            resModel != null && resModel.refId === orderData.clOrdID ? resModel : new OEDataModel();
          // Model Ids
          if (!modeIsDuplicate) {
            model.orderId = orderData.orderId != null ? orderData.orderId.toString() : null;
            model.orderRef = BfxConfigIDRefHelper.getOrderRef(orderData);

            // Ensure fwd netting ID is copied over when editing Scratchpad orders
            model.fwdNettingId = orderData.fwdNettingId;
          } else {
            model.orderId = null;
            model.orderRef = null;
            model.fwdNettingId = null;
          }
          model.strategyRef = orderData.strategyRef;

          model.refId = orderData.clOrdID;
          /// setting privatization flags

          if (modeIsModify || modeIsModifyInfo || modeIsAmendMarkup) {
            if (
              orderData.clientCode
                ? orderData.clientCode.indexOf("__bfx_privatization") == 0
                : false
            ) {
              logger.out.warn(
                `${this.constructor.name}.processOrderDataImplModel(orderId:${model.refId}) privatized`,
              );
              model.isModificationPrivatized = true;
              model.isModificationClientCommentsPrivatized = this.settings.privatizeClientComments;
              model.isModificationPrivateCommentsPrivatized =
                this.settings.privatizePrivateComments;
            }
            // client
            if (
              orderData.clientCode
                ? orderData.clientCode.indexOf("__bfx_privatization_restricted") == 0
                : false
            ) {
              logger.out.warn(
                `${this.constructor.name}.processOrderDataImplModel(orderId:${model.refId}) restricted`,
              );
              model.isClientRestricted = true;
            }
            // account
            if (
              orderData.accountCode
                ? orderData.clientCode.indexOf("__bfx_privatization_restricted") == 0
                : false
            ) {
              model.isAccountRestricted = true;
            }
          }
          // Pass Through Parameters
          if (
            modeIsModify &&
            Array.isArray(orderData.passthroughParameters) &&
            orderData.passthroughParameters.length > 0
          ) {
            model.passthroughParameters = orderData.passthroughParameters;
          }

          // STP Aggregation flag
          if ((modeIsModify || modeIsDuplicate) && orderData.stpAggregationEnabled != null) {
            let stpAgg: ConstraintValue = orderData.stpAggregationEnabled
              ? ConstraintValue.FlagEnabled
              : ConstraintValue.FlagDisabled;

            if (
              !this.settings.isSTPAggAvailable(
                orderData.requestedOrderType,
                orderData.executionInstruction,
              )
            ) {
              // absent rather than ConstraintValue.asInvalid, allows loading Client flag when order type changes
              stpAgg = ConstraintValue.None;
            }

            model.stpAggregationFlag = stpAgg;

            logger.out.info(
              `${this.constructor.name}.parseOrderDataImpl() orderData.stpAggregationEnabled: ${
                orderData.stpAggregationEnabled
              } model.stpAggregationFlag: ${ConstraintValue.info(model.stpAggregationFlag)}`,
            );
          }

          // States
          model.fillState = !modeIsDuplicate ? orderData.fillState : null;

          // OMS-37092
          let oeModel: any = model;
          if (
            model.srvOrderState != null &&
            oeModel._srvOrderStateTxTime != null &&
            orderData.txTime != null &&
            orderData.txTime.isBefore(oeModel._srvOrderStateTxTime)
          ) {
            logger.out
              .warn(`Received server order state update that is before the one contained in the model already ${
              orderData.txTime
            } < ${oeModel._srvOrderStateTxTime}. '
							+ 'Skipping update from ${CubiqTS.OrderState[model.srvOrderState]} to ${
                CubiqTS.OrderState[orderData.orderState]
              } for order ${orderData.clOrdID}`);
          } else {
            model.srvOrderState = !modeIsDuplicate ? orderData.orderState : null;
          }

          if (
            modeIsModify &&
            orderData.orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED
          ) {
            model.displayState = OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE;
          } else if (orderData.orderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED) {
            model.displayState = OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY;
          } else {
            model.displayState = OENDisplayState.SHOW_ORDER_ENTRY;
          }

          // HANDLE ALGO

          if (orderData.requestedOrderType === CubiqTS.RequestOrderType.ALGO) {
            model.entryType =
              orderData.product === CubiqTS.Product.NDF ? OENEntryType.NDF_ALGO : OENEntryType.ALGO;
            model.executionMode = CubiqTS.ExecutionType.AUTOMATIC;
          } else if (orderData.requestedOrderType === CubiqTS.RequestOrderType.BMARK) {
            model.entryType = OENEntryType.BENCHMARK;
            model.executionMode = orderData.executionType;
          } else if (orderData.strategyType === CubiqTS.StrategyType.GAMMA) {
            model.entryType = OENEntryType.GAMMA;
            model.executionMode = orderData.executionType;
          } else {
            if (CubiqTS.Product.NDF == orderData.product) {
              model.entryType = OENEntryType.NDF;
            } else {
              model.entryType = OENEntryType.SPOT;
            }
            model.algoType = null;
            model.executionMode = orderData.executionType;
          }

          // Model data
          model.orderType = this.utility.createAutocompleteForOrderType(
            orderData.requestedOrderType,
          );
          let productCode = orderData.isInvertedOrderFg
            ? orderData.nonBaseCode + orderData.baseCode
            : orderData.baseCode + orderData.nonBaseCode;
          model.ccyPair = productCode;

          if (useTraderGroupOnDuplicate || !modeIsDuplicate) {
            model.traderGroupCodeId = orderData.deskId != null ? orderData.deskId.toString() : null;
          }

          model.clientIntent = orderData.clientIntent;
          model.fixedCcy = new ICFixedCcyModel(orderData.fixedCode, productCode);

          // For FXBFXOM-7470 we want to always use the total base amount for all
          // order types (not just Algo) regradless of any part-filled status - server has been altered
          // to no longer add the fill amount to any request fixedAmount
          model.orderAmount =
            orderData.orderQty != null && orderData.orderQty instanceof CubiqTS.BigNumber
              ? orderData.orderQty
              : new CubiqTS.BigNumber(0);

          model.vwapAllInRt = orderData.vwapAllInRt;
          model.executedQty = orderData.executedQty;

          // Request rate (if not Algo)
          if (
            orderData.requestRt != null &&
            orderData.requestedOrderType !== CubiqTS.RequestOrderType.ALGO
          ) {
            let requestPrice = new CubiqTS.BigNumber(orderData.requestRt);
            model.requestPrice = requestPrice;
          }

          if (
            orderData.requestedOrderType === CubiqTS.RequestOrderType.TSL ||
            orderData.requestedOrderType === CubiqTS.RequestOrderType.TSL_ASK ||
            orderData.requestedOrderType === CubiqTS.RequestOrderType.TSL_BID
          ) {
            if (orderData.trailingStopDetails != null) {
              model.trailingPips = this.pricingService.getTrailingPips(orderData);
              if (orderData.trailingStopDetails.isInitialTrailingRateProvidedFg) {
                model.trailingTrigger = this.pricingService.getTrailingTrigger(orderData);
                model.trailingTriggerProvided = true;
              } else {
                model.trailingTrigger = null;
                model.trailingTriggerProvided = false;
              }
            }
          }

          // only if the user has privilege will we use the calculated markup on the order for duplicate
          let originalType = orderData.spotMarkupType;
          if (useMarkupOnDuplicate || !modeIsDuplicate) {
            if (!canUserChangeMarkup) {
              // workaround/overwrite for user with no edit permissions
              model.originalMarkup.type = orderData.spotMarkupType;
              model.originalMarkup.value = orderData.spotMarkupValue;
              model.originalMarkup.submitOriginalMarkup = true;
            }
            // markup
            model.markup = new ICMarkupModel();
            model.markup.reverseEngineer = orderData.isRateReverseEngineeringFg;
            if (
              isBenchmark &&
              orderData.reqMargin != null &&
              ZERO.isLessThanOrEqualTo(orderData.reqMargin) &&
              CubiqTS.MarkUpType.Decimal == orderData.reqMarginType
            ) {
              // BM bulked orders with unset margin round trip as 0 Decimal.
              model.markup.value = null;
              model.markup.textValue = "";
              model.markup.typeValue = null;
              model.markup.checkbox.enabled = false;
              model.markup.checkbox.value = null;
            } else if (orderData.reqMargin != null) {
              model.markup.value = orderData.reqMargin ? orderData.reqMargin : null;
              model.markup.textValue = orderData.reqMargin ? orderData.reqMargin.toString() : "";
              model.markup.typeValue =
                orderData.reqMarginType != undefined
                  ? new ICAutocompleteGenericModel(
                      orderData.reqMarginType,
                      this.markupTypeToLabel(orderData.reqMarginType),
                    )
                  : null;
              model.markup.checkbox.enabled = isBenchmark
                ? orderData.reqMargin.isGreaterThan("0")
                : true;
              model.markup.checkbox.value =
                orderData.reqMargin != null && orderData.reqMarginType != null ? true : false;
            } else if (
              orderData.spotMarkupValue != null &&
              orderData.spotMarkupValue != undefined &&
              orderData.spotMarkupType != null &&
              orderData.spotMarkupType != undefined
            ) {
              if (CubiqTS.MarkUpType.Decimal == orderData.spotMarkupType) {
                // Decimal does not exist on UI
                // not in use - see workaround above
                /* model.originalMarkup.type = orderData.spotMarkupType;
								model.originalMarkup.value = orderData.spotMarkupValue;
								model.originalMarkup.conversionToPipsApplied = true; */
                orderData.spotMarkupType = CubiqTS.MarkUpType.Pips;
                if (orderData.spotMarkupValue.isGreaterThan(0)) {
                  convertMarkupValueFromDecsToPips = true;
                }
              }
              model.markup.value = orderData.spotMarkupValue ? orderData.spotMarkupValue : null;
              model.markup.textValue = orderData.spotMarkupValue
                ? orderData.spotMarkupValue.toString()
                : "";
              model.markup.typeValue =
                orderData.spotMarkupType != undefined
                  ? new ICAutocompleteGenericModel(
                      orderData.spotMarkupType,
                      this.markupTypeToLabel(orderData.spotMarkupType),
                    )
                  : null;
              model.markup.checkbox.enabled = true;
              model.markup.checkbox.value =
                orderData.spotMarkupValue != null && orderData.spotMarkupType != null
                  ? true
                  : false;
            } else {
              model.markup.value = null;
              model.markup.textValue = "";
              model.markup.typeValue = null;
              model.markup.checkbox.enabled = true;
              model.markup.checkbox.value = false;
            }
          }
          this.oeLogger.outputDevLogToConsole(
            `MARKUP-LOAD: -> value: ${
              orderData.spotMarkupValue != null ? orderData.spotMarkupValue.toString() : "NULL"
            } | type-original: ${
              originalType != null ? CubiqTS.MarkUpType[originalType] : "NULL"
            } | type-now: ${
              orderData.spotMarkupType != null
                ? CubiqTS.MarkUpType[orderData.spotMarkupType]
                : "NULL"
            } | conversion: ${convertMarkupValueFromDecsToPips ? "YES" : "NO"}  `,
          );

          model.clientComment = new ICCommentsModel();
          model.clientComment.value = model.clientComment.manualComment =
            !obfuscateClientData && orderData.comments1 != null ? orderData.comments1 : "";
          model.privateComments = !obfuscateClientData ? orderData.comments2 : null;

          model.allowPartialFills =
            orderData.executionInstruction === CubiqTS.FillSupport.ALLOW_PARTIAL
              ? ConstraintValue.FlagEnabled
              : ConstraintValue.FlagDisabled;
          model.holdSTP = orderData.stpHold;

          model.autoForwardPrice = orderData.autoForwardPricingFg;
          model.privateOrder = orderData.isPrivate;

          model.oboClient = orderData.oboClient;
          model.oboAccount = orderData.oboAccount;

          if (orderData.fixingDetails) {
            this.parseFixingDetails(model, orderData.fixingDetails, orderData.fixingSourceCd);
          }

          model.bulked = false;
          model.bulking = false;

          if (
            isBenchmark &&
            Array.isArray(orderData.allocationData) &&
            orderData.allocationData.length > 1
          ) {
            model.allocations = orderData.allocationData
              .map((allocation) => {
                let data = new CubiqTS.AllocationRequestDataImpl();

                let isPrivatised: boolean =
                  allocation.accountCode.indexOf("__bfx_privatization") == 0;
                if (modeIsDuplicate && isPrivatised) {
                  // no point duplicating privatised allocations
                  return null;
                }

                if (modeIsModify || modeIsModifyInfo) {
                  data.allocationReferenceId = allocation.allocationReference;
                }
                data.accountCode = allocation.accountCode;
                data.allocationIntent = allocation.allocationIntent;
                data.allocationQty =
                  (allocation.allocationQty != null && allocation.allocationQty) instanceof
                  CubiqTS.BigNumber
                    ? allocation.allocationQty.toString()
                    : null;
                data.fixedCcy = allocation.fixedCcy;
                // will be overriden by ticket
                // data.markupQty = (allocation.markupQty != null && allocation.markupQty) instanceof CubiqTS.BigNumber ?
                //   allocation.markupQty.toFormat(2) : null;
                // data.markupType = allocation.markupType;
                // data.oboAccount = allocation.oboAccount;
                if (
                  allocation.settleTenor != null &&
                  CubiqTS.Tenor.EMPTY !== allocation.settleTenor
                ) {
                  data.settleTenor = allocation.settleTenor;
                } else if (allocation.settleDate != null) {
                  let settleDate = new CubiqTS.StandardDateImpl();
                  settleDate.day = allocation.settleDate.date();
                  settleDate.month = allocation.settleDate.month() + 1;
                  settleDate.year = allocation.settleDate.year();
                  data.settleDate = settleDate;
                }
                model.bulking = true;
                return data;
              })
              .filter(
                (obj) => {
                  return null != obj;
                } /* remove any duds returned from the map above*/,
              );
          }

          // Activation Details
          let goodFrom: ICGoodFromUntilModel = null;

          if (orderData.activationDetails != null) {
            const activationDetails: CubiqTS.ActivationDetailsImpl = orderData.activationDetails;
            let marketTimeConstant: CubiqTS.MarketTimeConstantImpl = null;
            let alias = activationDetails.timeAlias;

            if (alias != null && alias != "CUSTOM") {
              // Set alias to CUSTOM if we have a time but no valid ID
              marketTimeConstant = this.timeMachine.getMarketTimeConstantImpl(alias);
              if (
                marketTimeConstant == null &&
                activationDetails.activationTime != null &&
                activationDetails.activationDate != null &&
                activationDetails.timezone != null
              ) {
                logger.out.info(`Activation: Rewriting Market Alias [${alias}] as CUSTOM `);
                alias = "CUSTOM";
              }
            }

            if (alias != null && alias != "CUSTOM" && marketTimeConstant != null) {
              goodFrom = this.timeMachine.createModelFromMarketTimeConstantImpl(marketTimeConstant);
              goodFrom.isLoadPending = true;
            } else if (
              activationDetails.activationTime != null &&
              activationDetails.activationDate != null &&
              activationDetails.timezone != null
            ) {
              goodFrom = new ICGoodFromUntilModel();
              goodFrom.isLoadPending = true;
              goodFrom.timeValue = activationDetails.activationTime;
              goodFrom.marketValue = alias != null ? alias : "CUSTOM";
              goodFrom.dateValue = this.extractDateValue(
                activationDetails.activationDate,
                activationDetails,
              );
              goodFrom.isCustom = alias == "CUSTOM";
              goodFrom.timezoneValue = activationDetails.timezone;
            }
            goodFrom = this.validator.isGoodFromInThePast(goodFrom) ? null : goodFrom;
          }
          model.goodFrom = goodFrom;

          // Expiry Details
          let goodUntil: ICGoodFromUntilModel = null;
          if (orderData.expiryDetails != null) {
            const expiryDetails: CubiqTS.ExpiryDetailsImpl = orderData.expiryDetails;
            let marketTimeConstant: CubiqTS.MarketTimeConstantImpl = null;
            let alias = expiryDetails.timeAlias;

            if (alias != null && alias != "CUSTOM") {
              // Set alias to CUSTOM if we have a time but no valid ID
              marketTimeConstant = this.timeMachine.getMarketTimeConstantImpl(alias);
              if (
                marketTimeConstant == null &&
                expiryDetails.expiryTime != null &&
                expiryDetails.expiryDate != null &&
                expiryDetails.timezone != null
              ) {
                logger.out.info(`Expiry: Rewriting Market Alias [${alias}] as CUSTOM `);
                alias = "CUSTOM";
              }
            }
            if (marketTimeConstant != null && alias == "GTC") {
              goodUntil =
                this.timeMachine.createModelFromMarketTimeConstantImpl(marketTimeConstant);
              goodUntil.isLoadPending = true;
            } else if (
              alias != null &&
              alias != "CUSTOM" &&
              marketTimeConstant != null &&
              this.timeMachine.validateMarketTimeConstantImpl(marketTimeConstant)
            ) {
              goodUntil =
                this.timeMachine.createModelFromMarketTimeConstantImpl(marketTimeConstant);
              goodUntil.isLoadPending = true;
            } else if (
              expiryDetails.expiryTime != null &&
              expiryDetails.expiryDate != null &&
              expiryDetails.timezone != null
            ) {
              goodUntil = new ICGoodFromUntilModel();
              goodUntil.timeValue = expiryDetails.expiryTime;
              goodUntil.marketValue = alias != null ? alias : "CUSTOM";
              goodUntil.dateValue = this.extractDateValue(expiryDetails.expiryDate, expiryDetails);
              goodUntil.timezoneValue = expiryDetails.timezone;
              goodUntil.isCustom = true;
              goodUntil.isLoadPending = true;
            }
          }
          model.goodUntil = goodUntil;

          // Suspension
          const suspensionDetails = this.suspensionHelper.getEntrySuspensionDetails(orderData);
          if (suspensionDetails) {
            try {
              model.suspend = new ICSuspendModel();
              model.suspend.activated = true;
              // SUSPEND FROM
              model.suspend.suspendFrom = new ICTimeControlModel();
              model.suspend.suspendTo = new ICTimeControlModel();

              model.suspend.suspendFrom.timeValue = suspensionDetails.fromTime;
              model.suspend.suspendTo.timeValue = suspensionDetails.toTime;

              let tz = suspensionDetails.fromTimeTZ;
              model.suspend.suspendFrom.timezoneValue = new ICAutocompleteGenericModel(tz, tz);
              model.suspend.suspendFrom.timezoneModel = this.timeMachine.getTimeZoneImpl(tz);

              tz = suspensionDetails.toTimeTZ;
              model.suspend.suspendTo.timezoneValue = new ICAutocompleteGenericModel(tz, tz);
              model.suspend.suspendTo.timezoneModel = this.timeMachine.getTimeZoneImpl(tz);
            } catch (ex) {
              logger.out.error(
                `ERROR: _oeReverseAdapter.parseOrderDataImpl(): Exception processing suspension details: ${ex}`,
              );
              model.suspend = null;
            }
          }

          // privatization
          let privatizationFg = StaticDataPrivatizationType.hasPrivatization(orderData);
          if (modeIsModify || modeIsModifyInfo || modeIsAmendMarkup) {
            model.isModificationPrivatized = privatizationFg;
          } else {
            model.isModificationPrivatized = false;
          }

          model.customValues = orderData.customValues;

          ////////////////////////////////////////////////
          // Lookups
          let promiseArray = new Array<Promise<any>>();
          // 0. Client
          promiseArray.push(
            this.lookup.client(orderData.clientCode, false).catch((error) => {
              privatizationFg
                ? logger.out.info(
                    `INFO: _oeReverseAdapter.parseOrderDataImpl(): Client Privatized!`,
                  )
                : logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
              return null;
            }),
          );
          // 1. Account
          promiseArray.push(
            this.lookup.account(orderData.accountCode, false).catch((error) => {
              privatizationFg
                ? logger.out.info(
                    `INFO: _oeReverseAdapter.parseOrderDataImpl(): Account Privatized!`,
                  )
                : logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
              return null;
            }),
          );
          // 2. Client Trader
          promiseArray.push(
            this.lookup.userById(orderData.clientTraderId).catch((error) => {
              logger.out.warn(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
              return null;
            }),
          );
          // 3. Client Contacts
          promiseArray.push(
            this.lookup.contacts(orderData.clientContactIds).catch((error) => {
              logger.out.warn(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
              return null;
            }),
          );
          // 4. CcyPair Model
          promiseArray.push(
            this.lookup.ccyPairPromise(productCode).catch((error) => {
              logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
              return null;
            }),
          );
          // 5. Trader Group
          let traderGroupIsIn: boolean = false;
          if (useTraderGroupOnDuplicate || !modeIsDuplicate) {
            promiseArray.push(
              this.lookup.traderGroupPromise(orderData.deskId).catch((error) => {
                logger.out.warn(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
            );
            traderGroupIsIn = true;
          }
          // 6. Algo Strategy
          let algoStrategyIsIn: boolean = false;
          if (
            orderData.requestedOrderType === CubiqTS.RequestOrderType.ALGO &&
            orderData.algoStrategyName != null &&
            orderData.algoStrategyName != ""
          ) {
            promiseArray.push(
              this.lookup.algoStrategy(orderData.algoStrategyName).catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
            );
            algoStrategyIsIn = true;
          }
          let fixingSourceIsIn: boolean = false;
          if (CubiqTS.Product.NDF == orderData.product) {
            if (!orderData.fixingSourceCd) {
              orderData.fixingSourceCd = "- CUSTOM -";
            }
            promiseArray.push(
              this.lookup
                .fixingSource(orderData.fixingSourceCd, orderData.baseCode + orderData.nonBaseCode)
                .catch((error) => {
                  logger.out.warn(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                  return null;
                }),
            );
            fixingSourceIsIn = true;
          }

          Promise.all(promiseArray).then(
            (values: Array<any>) => {
              if (modeIsDuplicate && !ClientAccountVisibilityType.isFull(values[0]))
                obfuscateClientData = true;

              /* PRIVATIZATION  */
              if (model.isModificationPrivatized) {
                // CLIENT - extract meta data
                if (values[0] instanceof CubiqTS.ClientImpl) {
                  model.clientBranch = values[0].branchCd; // fix for fixing control
                }
                // CLIENT TRADER - determine if populated or not for privatization
                if (values[2] instanceof CubiqTS.UserImpl) {
                  model.isClientTraderPrivatized = true;
                }
                // CONTACTS - determine if populated or not for privatization
                if (values[3] instanceof CubiqTS.ClientContactImpl) {
                  model.isContactsPrivatized = true;
                }
              }

              model.client = !obfuscateClientData ? values[0] : null;
              //model.account = !obfuscateClientData ? values[1] : null; // account obfuscation is different
              model.account =
                modeIsDuplicate && !ClientAccountVisibilityType.isFull(values[1])
                  ? null
                  : values[1];

              model.clientTrader =
                !obfuscateClientData && values[2] != null
                  ? new ICAutocompleteGenericModel(
                      values[2],
                      values[2].userName,
                      model.isClientTraderPrivatized,
                    )
                  : null;
              model.contacts = !obfuscateClientData ? values[3] : null;
              if (
                orderData.orderType == CubiqTS.OrderType.ALGO &&
                model.client &&
                !model.client.autoFillEnabled
              ) {
                model.client = null;
                model.account = null;
                model.clientTrader = null;
                model.contacts = null;
              }
              model.ccyPairModel = values[4];
              if (convertMarkupValueFromDecsToPips) {
                let multiplier = this.utility.getReverseDecsToPipsMultiplier(
                  orderData,
                  model.ccyPairModel,
                );
                orderData.spotMarkupValue = new CubiqTS.BigNumber(
                  orderData.spotMarkupValue.times(multiplier).toFixed(1),
                );
                model.markup.value = orderData.spotMarkupValue ? orderData.spotMarkupValue : null;
                model.markup.textValue = orderData.spotMarkupValue
                  ? orderData.spotMarkupValue.toString()
                  : "";
              }
              // check the tenor and fwd trigger is valid for the pair
              this.checkTenorValidForOrder(model, orderData);
              this.checkFwdTriggerValidForOrder(model, orderData);

              if (values.length >= 6 && traderGroupIsIn) {
                model.traderGroup = values[5];
              }

              if (values.length >= 6 && algoStrategyIsIn) {
                let algoStrategyModel: CubiqTS.AlgoStrategyImpl = traderGroupIsIn
                  ? values[6]
                  : values[5];
                model.algoType = new ICAutocompleteGenericModel(
                  orderData.algoStrategyName,
                  algoStrategyModel.description,
                );
                // LIMIT PRICE
                if (
                  orderData.requestRt != null &&
                  orderData.requestedOrderType === CubiqTS.RequestOrderType.ALGO
                ) {
                  let limitPriceModel = new ICLimitPriceModel();
                  limitPriceModel.mode = this.atdlDataset.getLimitPriceMode(algoStrategyModel.id);
                  limitPriceModel.activated = true;
                  limitPriceModel.value = orderData.requestRt;
                  limitPriceModel.textValue = orderData.requestRt.toFixed(
                    this.lookup.ccyPairPrecision(productCode),
                  );
                  model.limitPrice = limitPriceModel;
                }
                // Strategy Parameters
                let algoModel = this.atdlDataset.get(orderData.algoStrategyName);
                if (algoModel instanceof ATDLDataModel) {
                  model.algoParameters = algoModel.parameters;

                  if (
                    orderData.strategyParameters instanceof Array &&
                    orderData.strategyParameters.length > 0
                  ) {
                    for (let strategyParameter of orderData.strategyParameters) {
                      // exclude 'StrategyName'
                      if (
                        strategyParameter instanceof CubiqTS.StrategyParameterImpl &&
                        strategyParameter.parameterName !== "StrategyName"
                      ) {
                        let modelParameter = model.algoParameters.find(
                          (item) => item.refName === strategyParameter.parameterName,
                        );
                        if (modelParameter instanceof ATDLParameterDataModel) {
                          if (modelParameter.constValue == null) {
                            this.assignParameterModelFromString(modelParameter, strategyParameter);
                          }
                        } else {
                          logger.out.error(
                            `ERROR: _oeReverseAdapter.parseOrderDataImpl(): Parameter '${strategyParameter.parameterName}' not found in model`,
                          );
                        }
                      }
                    }
                  }
                } else {
                  logger.out.error(
                    `ERROR: _oeReverseAdapter.parseOrderDataImpl(): ATDL definition for '${orderData.algoStrategyName}' NOT FOUND`,
                  );
                }
              } else {
                model.algoType = null;
              }

              if (values.length >= 6 && fixingSourceIsIn) {
                let fs =
                  traderGroupIsIn && algoStrategyIsIn
                    ? values[7]
                    : traderGroupIsIn || algoStrategyIsIn
                      ? values[6]
                      : values[5];
                if (fs) {
                  model.fixingSource = new ICFixingSourceModel(
                    fs.label,
                    fs.label,
                    fs.id,
                    fs.pair,
                    fs.isDefault,
                  );
                }
              }
              if (modeIsDuplicate && model.client == null) {
                // duplicate might not allow entry for the client,
                // client will be null so we remove any comments also
                model.clientComment.value = null;
                model.clientComment.defaultComment = null;
                model.clientComment.manualComment = null;
                model.clientComment.clientTraderComment = null;
                model.privateComments = null;
              } else {
                // derived value = default client comment
                model.clientComment.defaultComment =
                  model.client != null && model.client instanceof CubiqTS.ClientImpl
                    ? model.client.defaultClientComment
                    : null;
                // derived value = client trader comment
                model.clientComment.clientTraderComment =
                  values[2] != null && values[2].comment != null && values[2].comment.length > 0
                    ? `${values[2].userName}: ${values[2].comment}`
                    : null;
                model.clientComment.manualComment =
                  this.utility.removeClientTraderCommentFromManualComment(
                    model.clientComment.manualComment,
                    model.clientComment.clientTraderComment,
                  );
              }

              if (modeIsDuplicate && isGamma) {
                try {
                  this.gammaAdapterHelper.parseGammaOrder(model, orderData);
                } catch (e) {
                  logger.out.error(
                    `${this.constructor.name}: Error parsing gamma strategy ` +
                      `for order ${orderData.orderRef}, ${e}`,
                  );
                }
              }

              logger.out.debug(
                `DEBUG: _oeReverseAdapter.parseOrderDataImpl(): Model conversion OrderDataImpl/OrderEntryModel: %O`,
                orderData,
                model,
              );
              resolve(model);
            },
            (reason) => {
              reject(`Model adaptation failed: ${reason}`);
            },
          );
        } else {
          reject(`Invalid orderData`);
        }
      } catch (ex) {
        reject(`Exception: ${ex}`);
      }
    });
  }

  private extractDateValue(
    date: string,
    details: CubiqTS.ExpiryDetailsImpl | CubiqTS.ActivationDetailsImpl,
  ): string {
    let value = CubiqTS.moment(date, "YYYY-MM-DD");

    if (value.isValid()) {
      return value.format("DD-MMM-YYYY");
    }

    value = CubiqTS.moment(date, "YYYYMMDD");

    if (value.isValid()) {
      return value.format("DD-MMM-YYYY");
    }

    logger.out.warn("Invalid Date in Order Date %O", details);

    return null;
  }

  parseStrategyParameters(
    model: OEDataModel,
    algoModel: ATDLDataModel,
    strategyParameters: Array<CubiqTS.StrategyParameterImpl>,
  ): void {
    model.algoParameters = algoModel.parameters;

    if (strategyParameters instanceof Array && strategyParameters.length > 0) {
      for (let strategyParameter of strategyParameters) {
        // exclude 'StrategyName'
        if (
          strategyParameter instanceof CubiqTS.StrategyParameterImpl &&
          strategyParameter.parameterName !== "StrategyName"
        ) {
          let modelParameter = model.algoParameters.find(
            (item) => item.refName === strategyParameter.parameterName,
          );
          if (modelParameter instanceof ATDLParameterDataModel) {
            if (modelParameter.constValue == null) {
              this.assignParameterModelFromString(modelParameter, strategyParameter);
            }
          } else {
            logger.out.error(
              `ERROR: _oeReverseAdapter.parseOrderDataImpl(): Parameter '${strategyParameter.parameterName}' not found in model`,
            );
          }
        }
      }
    }
  }

  parseFixingDetails(
    model: OEDataModel,
    fixingDetails: CubiqTS.FixingDataImpl,
    fixingCode?: string,
  ) {
    model.fixingCode = fixingCode != null ? fixingCode : fixingDetails.reqFixingCd;

    // date is in YYYYMMDD format
    // need to build a Date object from it

    let fixingDate = CubiqTS.moment(fixingDetails.reqFixingDate, "YYYYMMDD", true).toDate();
    model.fixingDate = fixingDate;
    model.fixingTime = fixingDetails.reqFixingTime;
    model.fixingTimezone = fixingDetails.reqFixingTzCd;
  }

  private checkTenorValidForOrder(model: OEDataModel, orderData: CubiqTS.OrderDataImpl): void {
    if (this.isFwdorNdfAllowed(model, orderData)) {
      let localDate: Date = null;

      if (
        orderData.requestedOrderType == CubiqTS.RequestOrderType.BMARK &&
        orderData.settleTenor == null &&
        (orderData.orderState == null || orderData.orderState === CubiqTS.OrderState.SAVED)
      ) {
        localDate = orderData.settleDate.toDate();
      } else if (orderData.settleDate) {
        const localMoment = this.convertUTCDateMoment(orderData.settleDate);
        localDate = localMoment.toDate();
      }
      model.valueDate = new ICTenorValueDateModel(orderData.settleTenor, localDate);
    } else {
      model.valueDate = new ICTenorValueDateModel(CubiqTS.Tenor.SPOT, null);
    }

    if (model.valueDate.tenor == null && orderData.fixingDetails == null) {
      // NDF with broken date
      model.fixingDate = orderData.fixingDate
        ? this.convertUTCDateMoment(orderData.fixingDate).toDate()
        : null;
    }
  }

  /**
   * Take a moment representing a midnight UTC date, and create a midnight local time moment
   * @param dateMoment
   * @returns
   */
  private convertUTCDateMoment(dateMoment) {
    const utcMoment = CubiqTS.moment(dateMoment).utc();
    return CubiqTS.moment(utcMoment.format("YYYY-MM-DD"));
  }

  private checkFwdTriggerValidForOrder(model: OEDataModel, orderData: CubiqTS.OrderDataImpl): void {
    if (
      this.isFwdorNdfAllowed(model, orderData) &&
      CubiqTS.RequestTrackingType.OUTRIGHT_RATE == orderData.requestTrackingType
    ) {
      model.trigger = new ICAutocompleteGenericModel(
        CubiqTS.RequestTrackingType.OUTRIGHT_RATE,
        "Outright",
      );
    } else {
      model.trigger = new ICAutocompleteGenericModel(CubiqTS.RequestTrackingType.SPOT_RATE, "Spot");
    }
  }

  private isFwdorNdfAllowed(model: OEDataModel, orderData: CubiqTS.OrderDataImpl): boolean {
    if (
      orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
      orderData.product === CubiqTS.Product.FWD &&
      model.ccyPairModel.allowBmarkFwdOrdersFg === true
    ) {
      return true;
    }
    if (
      (CubiqTS.Product.NDF == orderData.product &&
        (model.ccyPairModel.allowNdfSpotTriggerFg || model.ccyPairModel.allowNdfFwdTriggerFg)) ||
      ((CubiqTS.OrderType.LIMIT_CALL == orderData.orderType ||
        CubiqTS.OrderType.LIMIT_SL == orderData.orderType ||
        CubiqTS.OrderType.LIMIT_TP == orderData.orderType ||
        CubiqTS.OrderType.MARKET == orderData.orderType) &&
        CubiqTS.Product.FWD == orderData.product &&
        (model.ccyPairModel.allowFwdSpotTriggerFg || model.ccyPairModel.allowFwdFwdTriggerFg))
    ) {
      return true;
    }

    if (
      orderData.orderType === CubiqTS.OrderType.ALGO &&
      orderData.product === CubiqTS.Product.FWD &&
      model.ccyPairModel.allowAlgoFwdOrders === true
    ) {
      return true;
    }

    return false;
  }

  private markupTypeToLabel(type: CubiqTS.MarkUpType): string {
    switch (type) {
      case CubiqTS.MarkUpType.Percentage:
        return "%";
      case CubiqTS.MarkUpType.UsdPerMillion:
        return "$/m";
      case CubiqTS.MarkUpType.Pips:
        return "Pips";
    }
    return "Decimals";
  }

  private assignParameterModelFromString(
    modelParameter: ATDLParameterDataModel,
    strategyParameter: CubiqTS.StrategyParameterImpl,
  ): void {
    if (
      modelParameter instanceof ATDLParameterDataModel &&
      strategyParameter instanceof CubiqTS.StrategyParameterImpl &&
      modelParameter.refName === strategyParameter.parameterName
    ) {
      if (modelParameter.icdType in ATDLNControlType) {
        switch (modelParameter.icdType) {
          case ATDLNControlType.DROPDOWN_LIST:
            if (modelParameter.dataset instanceof Array) {
              let dataItem = modelParameter.dataset.find(
                (item) => item.wireValue === strategyParameter.parameterValue,
              );
              if (dataItem instanceof ATDLEnumModel) {
                let autocompleteModel = new ICDAutocompleteModel(dataItem.enumId, dataItem.uiRep);
                autocompleteModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
                modelParameter.value = autocompleteModel;
              } else {
              }
            }
            break;
          case ATDLNControlType.TEXTFIELD:
            let textModel = new ICDTextFieldModel(strategyParameter.parameterValue);
            textModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
            modelParameter.value = textModel;
            break;
          case ATDLNControlType.SINGLE_SPINNER:
            let spinnerModel = new ICDSpinnerModel();
            spinnerModel.textValue = strategyParameter.parameterValue;
            spinnerModel.value = new CubiqTS.BigNumber(strategyParameter.parameterValue);
            const atdlType = FIX_TO_PARAM_MAPPING.get(strategyParameter.parameterType);
            if (atdlType === ATDLNParameterType.PERCENT) {
              spinnerModel.value = spinnerModel.value.times(100);
            }
            spinnerModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
            modelParameter.value = spinnerModel;
            break;
          case ATDLNControlType.CHECKBOX:
            let checkboxModel = new ICDCheckboxModel();

            if (typeof strategyParameter.parameterValue === "string") {
              let enumModel = null;
              if (modelParameter.dataset instanceof Array && modelParameter.dataset.length > 0) {
                enumModel = modelParameter.dataset.find(
                  (item) => item.wireValue === strategyParameter.parameterValue,
                );
              }

              if (
                typeof modelParameter.checkedEnumRef === "string" &&
                typeof modelParameter.uncheckedEnumRef === "string"
              ) {
                checkboxModel.value =
                  modelParameter.checkedEnumRef === strategyParameter.parameterValue;
                checkboxModel.enumRef = checkboxModel.value
                  ? modelParameter.checkedEnumRef
                  : modelParameter.uncheckedEnumRef;
                if (enumModel == null) {
                  logger.out.warn(
                    `Restoring algo control value from boolean without explict enum set defined for parameter ${strategyParameter.parameterName}`,
                  );
                }
              } else if (
                typeof modelParameter.trueWireValue === "string" &&
                typeof modelParameter.falseWireValue === "string" &&
                (modelParameter.trueWireValue === strategyParameter.parameterValue ||
                  modelParameter.falseWireValue === strategyParameter.parameterValue)
              ) {
                checkboxModel.value =
                  strategyParameter.parameterValue === modelParameter.trueWireValue;
              } else if (
                strategyParameter.parameterValue === "N" ||
                strategyParameter.parameterValue === "Y" ||
                strategyParameter.parameterValue === "true" ||
                strategyParameter.parameterValue === "false"
              ) {
                checkboxModel.value =
                  strategyParameter.parameterValue === "Y" ||
                  strategyParameter.parameterValue === "true";
              } else {
                logger.out.error(
                  `WARNING: _oeReverseAdapter.assignParameterModelFromString(): Indeterminate Checkbox value: '${strategyParameter.parameterValue}', set to false`,
                );
                checkboxModel.value = false;
              }
            } else {
              logger.out.error(
                `WARNING: _oeReverseAdapter.assignParameterModelFromString(): Checkbox value '${strategyParameter.parameterValue}' not a string, set to false`,
              );
              checkboxModel.value = false;
            }
            checkboxModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
            modelParameter.value = checkboxModel;
            break;
          case ATDLNControlType.RADIO_LIST:
            if (
              typeof strategyParameter.parameterValue === "string" &&
              modelParameter.dataset instanceof Array &&
              modelParameter.dataset.length > 0
            ) {
              let dataItem = modelParameter.dataset.find(
                (item) => item.wireValue === strategyParameter.parameterValue,
              );
              if (dataItem instanceof ATDLEnumModel) {
                let radioModel = new ICDRadioInputModel();
                radioModel.value = dataItem.enumId;
                radioModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
                modelParameter.value = radioModel;
              }
            }
            break;
          case ATDLNControlType.CLOCK:
            let clockModel = new ICDClockModel();
            clockModel.value = strategyParameter.parameterValue;
            clockModel.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
            modelParameter.value = clockModel;
            break;
          case ATDLNControlType.LABEL:
            let mod = new ICDTextFieldModel(strategyParameter.parameterValue);
            mod.auxModel = this.createAuxModelBasedOnParameter(modelParameter);
            modelParameter.value = mod;
            break;
          default:
            logger.out.error(
              `ERROR: _oeReverseAdapter.assignParameterModelFromString(): Unsupported control type`,
            );
            break;
        }
      } else {
        logger.out.error(
          `ERROR: _oeReverseAdapter.assignParameterModelFromString(): Unknown ICD type '${modelParameter.icdType}' for parameter`,
        );
      }
    } else {
      logger.out.error(
        `ERROR: _oeReverseAdapter.assignParameterModelFromString(): Incompatible parameters, can NOT assign value`,
      );
    }
  }

  private createAuxModelBasedOnParameter(
    modelParameter: ATDLParameterDataModel,
  ): ICDAuxCheckboxModel {
    let auxModel: ICDAuxCheckboxModel = new ICDAuxCheckboxModel();
    if (modelParameter instanceof ATDLParameterDataModel && modelParameter.hasAuxiliaryCheckbox) {
      auxModel.enabled = true;
      auxModel.mode = ICDNAuxMode.OPTIONAL;
      auxModel.value = true;
      /* }else if(modelParameter.icdType === ATDLNControlType.CHECKBOX || modelParameter.icdType === ATDLNControlType.LABEL || modelParameter.icdType === ATDLNControlType.RADIO_LIST){
			auxModel.enabled = false;
			auxModel.mode = ICDNAuxMode.DISABLED;
			auxModel.value = true; */
    } else {
      auxModel.enabled = false;
      auxModel.mode = ICDNAuxMode.REQUIRED;
      auxModel.value = false;
    }
    return auxModel;
  }

  /**********************************************************************/
  /* ORDER DATA IMPL -Traversal */

  public findOrderDataForRefId(
    refId: string,
    rootOrderData: CubiqTS.OrderDataImpl,
  ): CubiqTS.OrderDataImpl {
    let matchedOrderData: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();
    let traversedNodes: Array<string> = new Array<string>();
    this.findOrderDataInStrategyStructure(refId, rootOrderData, traversedNodes, matchedOrderData);
    return matchedOrderData != null && matchedOrderData instanceof CubiqTS.OrderDataImpl
      ? matchedOrderData
      : null;
  }

  public checkIfRefIdPresentInOrderData(
    refId: string,
    rootOrderData: CubiqTS.OrderDataImpl,
  ): boolean {
    let matchedOrderData: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();
    let traversedNodes: Array<string> = new Array<string>();
    this.findOrderDataInStrategyStructure(refId, rootOrderData, traversedNodes, matchedOrderData);
    return matchedOrderData != null && matchedOrderData instanceof CubiqTS.OrderDataImpl;
  }

  public findRelationshipsForRefId(
    refId: string,
    rootOrderData: CubiqTS.OrderDataImpl,
  ): Array<OEStrategyRefModel> {
    let matchedOrderData: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();
    let traversedNodes: Array<string> = new Array<string>();
    this.findOrderDataInStrategyStructure(refId, rootOrderData, traversedNodes, matchedOrderData);
    return matchedOrderData != null && matchedOrderData instanceof CubiqTS.OrderDataImpl
      ? this.findRelationshipsForNode(matchedOrderData)
      : null;
  }

  private findRelationshipsForNode(orderData: CubiqTS.OrderDataImpl): Array<OEStrategyRefModel> {
    let refId = orderData.clOrdID;
    let relationships: Array<OEStrategyRefModel> = new Array<OEStrategyRefModel>();
    if (refId != null && orderData != null && orderData instanceof CubiqTS.OrderDataImpl) {
      // siblings
      if (
        orderData.siblingRelationship != null &&
        orderData.siblingRelationship instanceof CubiqTS.Relationship &&
        orderData.siblingRelationship.order != null &&
        orderData.siblingRelationship.order instanceof CubiqTS.OrderDataImpl &&
        !(
          relationships.filter((element) => {
            return element.refId === orderData.siblingRelationship.order.clOrdID;
          }).length > 0
        )
      ) {
        relationships.push({
          refId: orderData.siblingRelationship.order.clOrdID,
          type: orderData.siblingRelationship.relationshipType,
        });
      }
      let skipChildren: Array<string> = new Array<string>();
      // children
      if (
        orderData.childRelationships != null &&
        Array.isArray(orderData.childRelationships) &&
        orderData.childRelationships.length > 0
      ) {
        orderData.childRelationships.forEach((item: CubiqTS.Relationship) => {
          if (
            item != null &&
            item instanceof CubiqTS.Relationship &&
            item.order != null &&
            item.order instanceof CubiqTS.OrderDataImpl &&
            !(relationships.filter((element) => element.refId === item.order.clOrdID).length > 0)
          ) {
            // add OCO skip logic here
            if (
              item.order.siblingRelationship != null &&
              item.order.siblingRelationship instanceof CubiqTS.Relationship &&
              orderData.childRelationships.filter(
                (element) => element.order.clOrdID === item.order.siblingRelationship.order.clOrdID,
              ).length > 0
            ) {
              skipChildren.push(item.order.siblingRelationship.order.clOrdID);
            }

            if (!skipChildren.includes(item.order.clOrdID)) {
              relationships.push({
                refId: item.order.clOrdID,
                type: item.relationshipType,
              });
            }
          }
        });
      }
      return relationships;
    } else {
      return null;
    }
  }

  /**
   * Finds OrderDataImpl that has refId (clOrdId) identical to the one passes as first parameter.
   * @param queryRefId - refId to be matched
   * @param orderData - orderData to be searched, could be complex with multiple nested strategy legs
   * @param traversedNodes - used for the recursion, contains array of strategy legs (nodes) refIds to be explored
   * @param matchedOrderData - result matched OrderDataImpl
   */
  private findOrderDataInStrategyStructure(
    queryRefId: string,
    orderData: CubiqTS.OrderDataImpl,
    traversedNodes: Array<string>,
    matchedOrderData: CubiqTS.OrderDataImpl,
  ): void {
    let refId = orderData.clOrdID;
    if (
      matchedOrderData != null &&
      matchedOrderData instanceof CubiqTS.OrderDataImpl &&
      matchedOrderData.clOrdID != null &&
      matchedOrderData.clOrdID != ""
    ) {
      return;
    } else if (refId === queryRefId) {
      Object.assign(matchedOrderData, orderData);
      return;
    } else {
      traversedNodes.push(refId);
    }
    // traverse
    if (refId != null && orderData != null && orderData instanceof CubiqTS.OrderDataImpl) {
      let nodesToTraverse: Array<CubiqTS.OrderDataImpl> = new Array<CubiqTS.OrderDataImpl>();
      // add siblings
      if (
        orderData.siblingRelationship != null &&
        orderData.siblingRelationship instanceof CubiqTS.Relationship &&
        orderData.siblingRelationship.order != null &&
        orderData.siblingRelationship.order instanceof CubiqTS.OrderDataImpl &&
        !traversedNodes.includes(orderData.siblingRelationship.order.clOrdID)
      ) {
        nodesToTraverse.push(orderData.siblingRelationship.order);
      }
      // add children
      if (
        orderData.childRelationships != null &&
        Array.isArray(orderData.childRelationships) &&
        orderData.childRelationships.length > 0
      ) {
        orderData.childRelationships.forEach((item: CubiqTS.Relationship) => {
          if (
            item != null &&
            item instanceof CubiqTS.Relationship &&
            item.order != null &&
            item.order instanceof CubiqTS.OrderDataImpl &&
            !traversedNodes.includes(item.order.clOrdID)
          ) {
            nodesToTraverse.push(item.order);
          }
        });
      }

      nodesToTraverse.forEach((traverseItem) => {
        this.findOrderDataInStrategyStructure(
          queryRefId,
          traverseItem,
          traversedNodes,
          matchedOrderData,
        );
      });
    }
  }

  /**
   * Traverses a complex OrderDataImpl (that could contain multiple legs) and creates a Map<refId, OrderDataImpl> which contains all
   * individual order legs mapped to refId (clOrdId). The size of the Map indicates the number of strategy legs.
   * @param orderData - Complete OrderDataImpl model as received from the server.
   * @param OrderDataMap - method output, with all individual strategy legs (passed as parameter due to the recursion)
   */
  private traverseStrategyGraph(
    orderData: CubiqTS.OrderDataImpl,
    orderDataMap: Map<string, CubiqTS.OrderDataImpl>,
    mode: OENOperationMode,
    dupRefIdMap: Map<string, string> = new Map(),
  ): void {
    let refId = orderData.clOrdID;
    const origRefId = refId;

    if (mode === OENOperationMode.DUPLICATE) {
      if (!dupRefIdMap.has(origRefId)) {
        refId = this.utility.generateRandomRefId();
        dupRefIdMap.set(origRefId, refId);
      }

      refId = dupRefIdMap.get(origRefId);
      orderData.clOrdID = refId;
    }

    if (refId != null && !orderDataMap.has(refId)) {
      orderDataMap.set(refId, orderData);
    }

    let gamma: boolean =
      mode === OENOperationMode.DUPLICATE &&
      orderData != null &&
      orderData.strategyType === CubiqTS.StrategyType.GAMMA;

    if (
      refId != null &&
      orderData != null &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      !gamma
    ) {
      let newNodes: Array<CubiqTS.OrderDataImpl> = new Array<CubiqTS.OrderDataImpl>();
      // add siblings
      if (
        orderData.siblingRelationship != null &&
        orderData.siblingRelationship instanceof CubiqTS.Relationship &&
        orderData.siblingRelationship.order != null &&
        orderData.siblingRelationship.order instanceof CubiqTS.OrderDataImpl &&
        !orderDataMap.has(orderData.siblingRelationship.order.clOrdID)
      ) {
        newNodes.push(orderData.siblingRelationship.order);
      }
      // add children
      if (
        orderData.childRelationships != null &&
        Array.isArray(orderData.childRelationships) &&
        orderData.childRelationships.length > 0
      ) {
        orderData.childRelationships.forEach((item: CubiqTS.Relationship) => {
          if (
            item != null &&
            item instanceof CubiqTS.Relationship &&
            item.order != null &&
            item.order instanceof CubiqTS.OrderDataImpl &&
            !orderDataMap.has(item.order.clOrdID)
          ) {
            newNodes.push(item.order);
          }
        });
      }
      newNodes.forEach((newItem) => {
        // newNodes contains multiple refs to oco entries (once as a child then as sibling)
        this.traverseStrategyGraph(newItem, orderDataMap, mode, dupRefIdMap);
      });
    }
  }
}
